const ApiResponseUnmapper = require('@/libraries/DigiTickets/Api/ApiResponseUnmapper');
const axios = require('axios');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const parseLinkHeader = require('parse-link-header');
const qs = require('qs');
const { axiosErrorToApiError } = require('@/lib/Api/index');
const { removeNullValues } = require('@/functions/functions');

/**
 * Makes requests to the DigiTickets API.
 * This is intended to eventually replace angular-resource. For now it is useful to be able to get response headers
 * easily and to be able to request a specific complete URL.
 */
class ApiClient {
    /**
     * @param {AppConfig} appConfig
     * @param {ApiRequestParams} apiRequestParams
     */
    constructor(
        appConfig,
        apiRequestParams,
    ) {
        this.appConfig = appConfig;
        this.apiRequestParams = apiRequestParams;

        /**
         * @type {import('axios').AxiosInstance}
         */
        this.axios = axios.create({
            timeout: 30000,
        });

        // We could add the 'Cache-Control: no-cache' header to requests, but this currently results in the error:
        // Access to XMLHttpRequest at 'x' from origin 'y' has been blocked by CORS policy: Request header field pragma
        // is not allowed by Access-Control-Allow-Headers in preflight response.
        // The _ parameter solution below should be enough.
        // this.axios.defaults.headers = {
        //     'Cache-Control': 'no-cache',
        //     Pragma: 'no-cache',
        // };

        // Cache bust all requests by adding a '_' parameter to all of them with the current time,
        // the same way jQuery achieves this.
        // TODO: Do we really need this? Without this the browser can cache preflight OPTIONS responses and eliminate
        // the need for those on subsequent requests. We could at least change this to only be on GET requests.
        this.axios.interceptors.request.use((config) => {
            config.params = {
                ...config.params,
                _: new Date().getTime(),
            };

            return config;
        });

        this.log = logFactory('API');

        /**
         * This can be set to a function that will be called when any request error occurs.
         *
         * @type {function(import('axios').AxiosError)[]}
         */
        this.errorHandlers = [];
    }

    getDefaultMapperParams() {
        return this.apiRequestParams.defaultMappers();
    }

    /**
     * Un-map a mapped API response.
     *
     * @param {*} responseData The raw data returned from the API
     * @param {string} resourceType The type of resource(s) being unmapped. Purely for logging purposes.
     *
     * @return {[]}
     */
    unmapResponse(responseData, resourceType) {
        return ApiResponseUnmapper.transformApiResponse(responseData, resourceType);
    }

    /**
     * Convert a relative endpoint to an absolute URL.
     *
     * @param {string} endpoint
     *
     * @return {string}
     */
    createUrl(endpoint) {
        return this.appConfig.apiBaseUrl + endpoint;
    }

    /**
     * Make a request to an endpoint relative to the API root.
     * e.g. "orders" will make a request to "https://api.digitickets.co.uk/v2/orders".
     * Do not prefix the endpoint with a "/" because that will result in "https://api.digitickets.co.uk/v2//orders".
     *
     * @param {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} method
     * @param {string} endpoint Endpoint relative to API base URL.
     * @param {*} [data]
     * @param {boolean} [json] If true, will send the request as JSON instead of form encoding.
     * @param {Object<string, string>} [headers]
     *
     * @return {Promise<{headers: any, request?: any, data: any, statusText: string, links: null, config:
     *     import('axios').AxiosRequestConfig, status: number}>}
     */
    request(method, endpoint, data, json, headers = {}) {
        return this.requestUrl(
            method,
            this.createUrl(endpoint),
            data,
            json,
            headers,
        );
    }

    /**
     * Make a request to an absolute URL.
     *
     * @param {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} method
     * @param {string} url Absolute URL
     * @param {*} [data]
     * @param {boolean} [json] If true, will send the request as JSON instead of form encoding.
     * @param {Object<string, string>} [headers]
     *
     * @return {Promise<{headers: any, request?: any, data: any, statusText: string, links: null, config:
     *     import('axios').AxiosRequestConfig, status: number}>}
     */
    requestUrl(method, url, data, json = false, headers = {}) {
        // Query string data.
        let queryParams = {};

        // POST body data.
        let requestData = null;

        // Extract any existing params in the string URL to prevent duplicates, and allow overriding the default
        // param values.
        const splitUrlString = url.split('?');
        const queryParamsFromUrlString = qs.parse(splitUrlString[1]);
        // Set the url as only the part before the query.
        url = splitUrlString[0];

        // The query params become the default values + anything supplied in the URL.
        // To override default values from apiRequestParams add the new values to the URL.
        queryParams = {
            ...this.apiRequestParams.getValues(),
            ...queryParamsFromUrlString,
        };

        // Beware of differences between how AngularJS and Axios handle null values in data.
        // Angular will remove null values from the data object, but Axios will send them as an empty value.
        // See https://stackoverflow.com/questions/22315116/send-null-as-a-value-in-ngresource-angularjs
        // and https://github.com/angular/angular.js/blob/master/src/ng/http.js#L44
        //
        // To be compatible with what Angular did, we'll remove null and empty array values from the data object.
        //
        // We don't do this for JSON requests because these are all new and don't need to be compatible with what
        // Angular did. In those it may be desirable to send null/empty values.
        if (data && !json) {
            removeNullValues(data, true);
        }

        if (method === 'GET') {
            // For GET requests combine the data with the query params and put it all in the query string.
            queryParams = {
                ...queryParams,
                ...data,
            };
        } else {
            // For POST/PUT/PATCH/etc. requests the default query params go in the query string and the data
            // goes into the request body.
            // The typeof is so that this works in tests in Node.
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
                requestData = data;

                if (!headers['Content-Type']) {
                    headers['Content-Type'] = 'application/x-www-form-urlencoded';
                }
            } else if (json) {
                requestData = data;

                if (!headers['Content-Type']) {
                    headers['Content-Type'] = 'application/json';
                }
            } else {
                // If `data` is an object the request will be sent as application/json.
                // So we convert it to a string using the qs package
                // and it will be sent as application/x-www-form-urlencoded
                requestData = qs.stringify(data);

                if (!headers['Content-Type']) {
                    headers['Content-Type'] = 'application/x-www-form-urlencoded';
                }
            }
        }

        this.log.debug('Starting', method, url);

        const requestStart = new Date();
        return this.axios
            .request({
                method,
                url,
                headers,

                // `params` are the URL parameters (query string) to be sent with the request
                // Must be a plain object or a URLSearchParams object.
                // Note: If the `url` already contains a query string it will append to it, but it will not overwrite,
                // existing keys - you will end up with duplicates.
                params: queryParams,

                // `data` is the request body for POST/PATCH/etc. requests. For GET requests it does nothing.
                data: requestData,

                responseType: 'json',
            })
            .then((response) => {
                this.log.debug('Finished', method, url, 'Took ' + ((new Date()).getTime() - requestStart.getTime()) + 'ms.');
                return {
                    ...response,
                    // Parse the "Link" header in the response, which may contain a link to the next page of results.
                    links: this.extractLinks(response.headers),
                };
            })
            .catch(
                /**
                 * @param {AxiosError} err
                 *
                 * @throws {ApiError}
                 */
                (err) => {
                    const apiError = axiosErrorToApiError(err);

                    this.log.error(apiError);

                    // Deliver the error to any error handlers.
                    this.errorHandlers.forEach((handler) => handler(apiError));

                    // Re-throw the Error (not just a string!)
                    throw apiError;
                },
            );
    }

    /**
     * @param {{link?: string}} headers
     *
     * @return {{next?: {url: string}, prev?: {url: string}}}
     */
    extractLinks(headers) {
        // Note that Axios will convert all headers to lowercase: https://github.com/axios/axios/issues/413
        // So check for 'link' rather than 'Link'.
        if (headers && headers.link) {
            return parseLinkHeader(headers.link);
        }

        return {};
    }

    addErrorHandler(func) {
        this.errorHandlers.push(func);
    }
}

module.exports = ApiClient;
